import { Stack, TextField } from '@mui/material';
import type { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { CrudPage } from '../../shared/components/CrudPage';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { resources } from '../../core/services/resources';
import { useTenant } from '../../core/contexts/TenantContext';
import { formatDate } from '../../shared/utils/format';
import type {
  AIModel,
  GeoLocation,
  ReportInstance,
  ReportInstanceWeight,
} from '../../core/types/data-models';

/** Report instances have no name — label them by id + generate date. */
const reportInstanceLabel = (o: ReportInstance) =>
  `#${o.id}${o.generateDate ? ` — ${formatDate(o.generateDate)}` : ''}`;

export function ReportInstanceWeightsPage() {
  const { activeTenantId } = useTenant();

  const geosQuery = useQuery({
    queryKey: ['geo-locations', 'select'],
    queryFn: () => resources.geoLocations.list({ pageSize: 500, sort: 'name' }),
  });
  const modelsQuery = useQuery({
    queryKey: ['ai-models', 'select'],
    queryFn: () => resources.aiModels.list({ pageSize: 500, sort: 'name' }),
  });

  const geoNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of geosQuery.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [geosQuery.data]);
  const modelNameById = useMemo(() => {
    const m = new Map<number, string>();
    for (const r of modelsQuery.data?.data ?? []) m.set(r.id, r.name);
    return m;
  }, [modelsQuery.data]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      { field: 'reportInstanceId', headerName: 'Report instance', width: 140 },
      {
        field: 'geoLocationId',
        headerName: 'Geo location',
        flex: 1,
        minWidth: 180,
        renderCell: (p: GridRenderCellParams) => {
          const id = p.value as number | null | undefined;
          return id == null ? '' : geoNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'aiModelId',
        headerName: 'AI model',
        flex: 1,
        minWidth: 180,
        renderCell: (p: GridRenderCellParams) => {
          const id = p.value as number | null | undefined;
          return id == null ? '' : modelNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'weight',
        headerName: 'Weight',
        width: 120,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
      },
    ],
    [geoNameById, modelNameById],
  );

  return (
    <CrudPage<ReportInstanceWeight>
      title="Report instance weights"
      entityName="Instance weight"
      description="Visibility weight per geo location × AI model combination captured for one report instance."
      cacheKey={['report-instance-weights']}
      service={resources.reportInstanceWeights}
      columns={columns}
      initialMode="view"
      defaultValues={{
        reportInstanceId: undefined,
        geoLocationId: undefined,
        aiModelId: undefined,
        weight: 1,
        ...(activeTenantId != null ? { tenantId: activeTenantId } : {}),
      }}
      beforeSubmit={(v) => ({
        ...v,
        tenantId: v.tenantId ?? activeTenantId ?? undefined,
      })}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          <EntitySelect<ReportInstance>
            cacheKey={['report-instances', 'select']}
            service={resources.reportInstances}
            query={{ sort: '-id' }}
            getOptionLabel={reportInstanceLabel}
            label="Report instance"
            required
            value={values.reportInstanceId ?? null}
            onChange={(id) => setValues({ ...values, reportInstanceId: id ?? undefined })}
          />
          <EntitySelect<GeoLocation>
            cacheKey={['geo-locations', 'select']}
            service={resources.geoLocations}
            label="Geo location"
            required
            value={values.geoLocationId ?? null}
            onChange={(id) => setValues({ ...values, geoLocationId: id ?? undefined })}
          />
          <EntitySelect<AIModel>
            cacheKey={['ai-models', 'select']}
            service={resources.aiModels}
            label="AI model"
            required
            value={values.aiModelId ?? null}
            onChange={(id) => setValues({ ...values, aiModelId: id ?? undefined })}
          />
          <TextField
            label="Weight"
            type="number"
            required
            inputProps={{ step: 0.01 }}
            value={values.weight ?? ''}
            onChange={(e) =>
              setValues({ ...values, weight: e.target.value === '' ? undefined : Number(e.target.value) })
            }
          />
        </Stack>
      )}
    />
  );
}
